/**
 * The application's financial data model — one definition shared by storage (IndexedDB),
 * validation, the calculation layer, backups and sync packages. See ARCHITECTURE.md §3.
 *
 * Conventions: ids are UUIDs; dates are ISO strings (`YYYY-MM-DD`); accounting months are
 * `YYYY-MM`; amounts are decimal strings; timestamps are ISO date-times.
 */
import type { AttachmentOrigin } from "./attachments";
import type { DuplicateMatch } from "./duplicates";
import type { CategorySource, ExpenseAccountingRule, ExpenseSource, ExpenseStatus, SyncStatus } from "./expense";
import type { ReceiptSuggestion } from "./merchant-recognition";
import type { ExtractedField } from "./ocr/merge";
import type { OcrTextLine, ReceiptReading } from "./ocr/receipt-parser";
import type { IncomeAccountingRule, IncomeSource, IncomeStatus, IncomeType } from "./income";
import type { AccountingRule, PaymentMethodKind } from "./payment";

export type IsoDate = string;
export type YearMonth = string;
export type DecimalString = string;
export type IsoDateTime = string;

export interface Timestamps {
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

/** Excel synchronization state carried by every ledger record. */
export interface SyncState {
  status: SyncStatus;
  /** Last time the record was put in a sync package. */
  exportedAt: IsoDateTime | null;
  /** Record version confirmed as written to the workbook. */
  syncedVersion: number | null;
  syncedAt: IsoDateTime | null;
  error: string | null;
  excelRowHint: number | null;
}

// ─── Reference data (mirrors Excel "Elenco Spese") ──────────────────────────────────────────────

export interface CategoryGroup extends Timestamps {
  /** Deterministic from the exact label, so every device agrees on it. */
  id: string;
  excelLabel: string;
  sortOrder: number;
  excelRow: number;
  isActive: boolean;
}

export const COST_NATURES = ["fixed", "discretionary"] as const;
export type CostNature = (typeof COST_NATURES)[number];

export interface Category extends Timestamps {
  id: string;
  groupId: string;
  /** Exact Excel text, typos included (monthly sheets match it with SUMIFS). */
  excelLabel: string;
  sortOrder: number;
  excelRow: number;
  isActive: boolean;
  /** User-configured in Settings; never inferred (decision Q5). */
  costNature: CostNature | null;
}

export interface CategoryImportRecord {
  id: string;
  source: string;
  contentSha256: string;
  groupCount: number;
  categoryCount: number;
  diff: unknown;
  warnings: unknown[];
  importedAt: IsoDateTime;
}

export interface PaymentMethod extends Timestamps {
  id: string;
  name: string;
  kind: PaymentMethodKind;
  accountingRule: AccountingRule;
  /** Value for the Excel `Metodo Pagamento` column. */
  excelLabel: string;
  sortOrder: number;
  isActive: boolean;
}

// ─── Ledgers ────────────────────────────────────────────────────────────────────────────────────

export interface LegacyOrigin {
  workbookYear: number;
  /** Sheet and cell/row the value came from, for traceability. */
  sheet: string;
  cell: string;
  /** Original Excel formula, when the value was a formula (e.g. `=47.5/3`). */
  formula: string | null;
}

/**
 * Duplicate review of an expense (the user always decides; nothing is deleted):
 *  - none      no possible duplicate was found when the expense was saved
 *  - possible  saved while similar expenses exist, not reviewed yet
 *  - kept      the user saw the matches and intentionally kept this expense
 */
export const DUPLICATE_STATUSES = ["none", "possible", "kept"] as const;
export type DuplicateStatus = (typeof DUPLICATE_STATUSES)[number];

export interface DuplicateReview {
  status: DuplicateStatus;
  /** Matches found at the last check (snapshot: the other expenses are never modified). */
  matches: DuplicateMatch[];
  checkedAt: IsoDateTime | null;
  /** When the user decided to keep it. */
  resolvedAt: IsoDateTime | null;
}

export function emptyDuplicateReview(): DuplicateReview {
  return { status: "none", matches: [], checkedAt: null, resolvedAt: null };
}

export interface Expense extends Timestamps {
  id: string;
  version: number;
  status: ExpenseStatus;
  source: ExpenseSource;
  purchaseDate: IsoDate;
  /** False for imported rows whose Excel date may be the re-dated 1st of the month. */
  purchaseDateCertain: boolean;
  accountingMonth: YearMonth;
  accountingRule: ExpenseAccountingRule;
  paymentMethodId: string | null;
  amount: DecimalString;
  currency: string;
  amountEur: DecimalString;
  categoryId: string;
  categorySource: CategorySource;
  merchant: string | null;
  note: string | null;
  legacy: LegacyOrigin | null;
  confirmedAt: IsoDateTime | null;
  voidedAt: IsoDateTime | null;
  sync: SyncState;
  duplicate: DuplicateReview;
}

export interface Income extends Timestamps {
  id: string;
  version: number;
  status: IncomeStatus;
  source: IncomeSource;
  date: IsoDate;
  /** False for imported income: the workbook has no dates, only months. */
  dateCertain: boolean;
  accountingMonth: YearMonth;
  accountingRule: IncomeAccountingRule;
  type: IncomeType;
  description: string;
  amount: DecimalString;
  notes: string | null;
  legacy: LegacyOrigin | null;
  voidedAt: IsoDateTime | null;
  sync: SyncState;
}

// ─── Budget and balance ─────────────────────────────────────────────────────────────────────────

export type PlanningSource = "manual" | "excel_import";

export interface BudgetLine extends Timestamps {
  /** `${accountingMonth}|${categoryId}` — one line per category and month. */
  id: string;
  accountingMonth: YearMonth;
  categoryId: string;
  amount: DecimalString;
  source: PlanningSource;
}

export interface IncomeBudget extends Timestamps {
  /** = accountingMonth */
  id: YearMonth;
  accountingMonth: YearMonth;
  salary: DecimalString;
  extra: DecimalString;
  source: PlanningSource;
}

/**
 * A known balance at the start of a month (e.g. "Saldo di Partenza" = €4,751.58 at 2026-01).
 * It sets that month's opening balance; later months carry forward from it.
 */
export interface BalanceAnchor extends Timestamps {
  id: string;
  effectiveMonth: YearMonth;
  amount: DecimalString;
  label: string;
  source: PlanningSource;
}

// ─── Images and drafts (local only; never uploaded) ─────────────────────────────────────────────

/**
 * One stored image. `data` is the single kept copy (see planImageProcessing in
 * domain/attachments.ts); the original file is described by `original` but not stored.
 */
export interface Attachment extends Timestamps {
  id: string;
  /** A draft until the expense is saved, then the expense (same id: the draft id becomes the expense id). */
  ownerKind: "draft" | "expense";
  ownerId: string;
  origin: AttachmentOrigin;
  mimeType: string;
  data: ArrayBuffer;
  byteSize: number;
  width: number;
  height: number;
  /** sha-256 of `data`: identifies the same image picked twice (future duplicate checks). */
  sha256: string;
  processing: "keep" | "resize" | "reencode";
  original: { name: string | null; type: string; byteSize: number; width: number; height: number; lastModified: number | null };
}

/** Form values of an expense being entered, persisted so a captured image and typed values survive a reload. */
export interface DraftForm {
  amount: string;
  purchaseDate: string;
  paymentMethodId: string;
  categoryId: string;
  merchant: string;
  note: string;
  overrideEnabled: boolean;
  override: string;
}

export interface ExpenseDraft extends Timestamps {
  /** Becomes the id of the saved expense. */
  id: string;
  /** How the entry started; decides the expense `source` (camera → receipt, library → screenshot). */
  origin: "manual" | AttachmentOrigin;
  form: DraftForm | null;
}

/**
 * What on-device OCR read from one image (ARCHITECTURE.md §16). A prediction, kept apart from the
 * expense: it follows its attachment (draft → expense) and is deleted with it.
 */
export interface Extraction extends Timestamps {
  id: string;
  attachmentId: string;
  /** The image that was read: a replaced image never reuses this result. */
  attachmentSha256: string;
  engine: string;
  status: "done" | "failed";
  text: string;
  lines: OcrTextLine[];
  reading: ReceiptReading | null;
  suggestion: ReceiptSuggestion | null;
  error: string | null;
  durationMs: number;
  /** Values written into the draft form automatically (shown with a "verifica" note). */
  applied: Partial<Record<ExtractedField, string>>;
  appliedAt: IsoDateTime | null;
  /** What the user saved: measures how well OCR did. */
  final: { amount: DecimalString; purchaseDate: IsoDate; merchant: string | null; categoryId: string; paymentMethodId: string | null } | null;
  reviewedAt: IsoDateTime | null;
}

// ─── Housekeeping ───────────────────────────────────────────────────────────────────────────────

export interface Setting {
  key: string;
  value: unknown;
}

export type TransferKind = "sync-export" | "sync-import" | "backup-export" | "backup-restore" | "workbook-import" | "excel-copy";

export interface TransferLogEntry {
  id: string;
  kind: TransferKind;
  createdAt: IsoDateTime;
  summary: Record<string, unknown>;
}

export function emptySyncState(status: SyncStatus = "not_queued"): SyncState {
  return { status, exportedAt: null, syncedVersion: null, syncedAt: null, error: null, excelRowHint: null };
}
